import { useOutletContext, useSearchParams } from "react-router-dom";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTableSort from "components/Table/useTableSort";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import { SearchQueryOrderDirection } from "types/generated";

import { useLayoutActions } from "../../hooks/useLayoutActions";
import { useLayoutHeading } from "../../hooks/useLayoutHeading";
import { IntegrationView } from "../../types";
import AiCreateIntegrationButton from "./components/CreateIntegrationButton";
import { INTEGRATIONS_AI_TABLE_CELLS } from "./components/Table/Cells";
import useIntegrationsAiTableStore from "./components/Table/tableStore";
import { AI_COLUMN_ID, AI_TABLE_FILTERS_STORAGE_KEY, SORT_CONFIG } from "./constants";
import AiIntegrationsEmpty from "./Empty";
import { useFilteredIntegrations } from "./useFilteredEntities";
import useSearchAiIntegrations from "./useSearchAiIntegrations";

const AiIntegrationsList = () => {
  const trackAnalytics = useAnalytics();

  const { isPageLoading, error, integrations, refetch } = useSearchAiIntegrations();
  const { integration } = useOutletContext<{ integration: IntegrationView }>();

  useLayoutHeading(integration.name);

  useBreadcrumbs([
    {
      title: "Integrations",
      link: "/integrations",
    },
    {
      title: integration.name,
    },
  ]);

  useLayoutActions(<AiCreateIntegrationButton onCreated={refetch} />);

  const [searchParams] = useSearchParams();

  const isSearchActive = !!getSearchQuery(searchParams);

  const { sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: AI_TABLE_FILTERS_STORAGE_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption: SORT_CONFIG[AI_COLUMN_ID.NAME],
    initialSortDirection: SearchQueryOrderDirection.Asc,
  });

  const filteredIntegrations = useFilteredIntegrations(integrations, sortDescriptor);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <TableSimple
      data={filteredIntegrations}
      loading={isPageLoading}
      store={useIntegrationsAiTableStore}
      ariaLabel="AI integrations list"
      trackAnalytics={trackAnalytics}
      cellPerColumn={INTEGRATIONS_AI_TABLE_CELLS}
      filtersStorageKey={AI_TABLE_FILTERS_STORAGE_KEY}
      sortConfig={SORT_CONFIG}
      emptyState={
        isSearchActive ? (
          <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
        ) : (
          <AiIntegrationsEmpty />
        )
      }
    />
  );
};

export default AiIntegrationsList;
